import React from "react";
import toast from "react-hot-toast";
import useApi from "../useApi";

export interface Note {
  _id: string;
  noteCategory: "clinical" | "non-clinical";
  richText: string;
  createdTimestamp: number;
  isImportant?: "yes" | "no";
}

interface PatientNotesResponse {
  msg: string;
  data: Note[];
  status: string;
  patientBillingMinuteMessage: string;
}

const useFetchPatientNotes = (billingCycleId?: string) => {
  const [notes, setNotes] = React.useState<Note[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const patientBillingMinuteMessageRef = React.useRef<string>("");

  const { makeApiCall } = useApi({
    url: `/rtm/patient-dashboard/pdf/v3/note/${billingCycleId}`,
    method: "GET",
  });

  const fetchNotes = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { response, error } = await makeApiCall<
        undefined,
        PatientNotesResponse
      >();

      if (response?.status === "success" && response.data) {
        setNotes(response.data);
        patientBillingMinuteMessageRef.current =
          response.patientBillingMinuteMessage || "";
      } else {
        setNotes([]);
        setError(error?.msg || "Failed to fetch patient notes");
        patientBillingMinuteMessageRef.current = "";
      }
    } catch (err) {
      setNotes([]);
      const errorMessage = "An error occurred while fetching patient notes";
      setError(errorMessage);
      patientBillingMinuteMessageRef.current = "";
    } finally {
      setLoading(false);
    }
  }, [billingCycleId, makeApiCall]);
  React.useEffect(() => {
    if (billingCycleId) {
      fetchNotes();
    } else {
      setNotes([]);
      patientBillingMinuteMessageRef.current = "";
    }
  }, [fetchNotes, billingCycleId]);

  const refetch = React.useCallback(() => {
    return fetchNotes();
  }, [fetchNotes]);

  return {
    notes,
    loading,
    error,
    refetch,
    patientBillingMinuteMessage: patientBillingMinuteMessageRef.current,
  };
};

export default useFetchPatientNotes;
